import { Button, Flex, Text } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { FunctionComponent, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOrganization } from "src/hooks/useOrganization";
import { AutomaticMessageFormType } from "src/scenes/orgAdmin/formTemplates/types/editMessagesDraft";
import {
  FORM_TEMPLATE_TAB_HASH,
  FormTemplates,
} from "src/services/url/OrgAdmin";
import { EditFormTemplateActionBar } from "../../../components/ActionBar";
type Props = {
  isPreview: boolean;
  isViewOnly: boolean;
  setIsPreview: (value: boolean) => void;
  formId: string;
  formTemplateId: string;
  schoolId: string | null;
  hasChanges: boolean;
  onClearChanges: () => void;
  isSubmitting: boolean;
  onConfirmBack: () => Promise<boolean>;
};

/**
 * When editing a Message Template, users can preview their changes.
 * Going between editing and preview looks like a navigation event, but is not.
 * The parent page simply toggles the edit page and preview page.
 */
export const EditMessageTemplateActionBar: FunctionComponent<Props> = (
  props
) => {
  const organization = useOrganization();
  const formik = useFormikContext<AutomaticMessageFormType>();
  const navigate = useNavigate();

  const { enrollmentPeriodId = "" } = useParams();
  const {
    isPreview,
    setIsPreview,
    formId,
    formTemplateId,
    schoolId,
    isViewOnly,
    hasChanges,
    onClearChanges,
    isSubmitting,
    onConfirmBack,
  } = props;

  const schoolIdParam = schoolId ? `?schoolId=${schoolId}` : "";

  const backRoute = organization
    .map(
      (org) =>
        `${FormTemplates.edit(
          org,
          enrollmentPeriodId,
          formTemplateId
        )}${schoolIdParam}#${FORM_TEMPLATE_TAB_HASH.AUTOMATIC_MESSAGES}`
    )
    .withDefault("#");

  const handleBackClick = useCallback(async () => {
    if (hasChanges) {
      const confirmed = await onConfirmBack();
      if (!confirmed) {
        return;
      }
    }
    navigate(backRoute);
  }, [hasChanges, onConfirmBack, navigate, backRoute]);

  return (
    <EditFormTemplateActionBar hasChanges={hasChanges}>
      {isViewOnly || !isPreview ? (
        <Button variant="outline" onClick={handleBackClick} colorScheme="gray">
          Back
        </Button>
      ) : (
        <Button
          variant="outline"
          onClick={() => setIsPreview(false)}
          colorScheme="gray"
        >
          Back
        </Button>
      )}

      <Flex direction="column" gap={2}>
        <Flex gap={2}>
          {hasChanges && (
            <Button
              variant="ghost"
              colorScheme="gray"
              onClick={onClearChanges}
              gridColumn="3"
            >
              Clear unpublished changes
            </Button>
          )}

          {isPreview ? (
            <Button
              form={formId}
              type="submit"
              isDisabled={
                isViewOnly || !formik.isValid || !hasChanges || isSubmitting
              }
              isLoading={isSubmitting}
            >
              Save
            </Button>
          ) : (
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                formik.validateForm().then((errors) => {
                  setIsPreview(Object.keys(errors).length === 0);
                });
              }}
              isLoading={isSubmitting}
            >
              Preview and save
            </Button>
          )}
        </Flex>
        {hasChanges && (
          <Text textAlign="right" fontSize="xs" color="gray.500">
            Unpublished changes
          </Text>
        )}
      </Flex>
    </EditFormTemplateActionBar>
  );
};
